import * as React from "react";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";

export const PAGE_SIZE = 25;

export const INVOICE_STATUSES = ["draft", "approved", "sent", "part_paid", "paid", "written_off"];

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft: { bg: "#fff3e0", color: "#e65100" },
  approved: { bg: "#e3f2fd", color: "#1565c0" },
  sent: { bg: "#f3e5f5", color: "#6a1b9a" },
  part_paid: { bg: "#fffde7", color: "#8d6e00" },
  paid: { bg: "#e8f5e9", color: "#2e7d32" },
  written_off: { bg: "#efebe9", color: "#4e342e" },
  open: { bg: "#fce4ec", color: "#c62828" },
  pending_approval: { bg: "#fff3e0", color: "#e65100" },
  holding: { bg: "#e3f2fd", color: "#1565c0" },
  sprive_funded: { bg: "#f3e5f5", color: "#6a1b9a" },
  cancelled: { bg: "#eceff1", color: "#455a64" },
  active: { bg: "#e8f5e9", color: "#2e7d32" },
  inactive: { bg: "#eceff1", color: "#455a64" },
  matched: { bg: "#e8f5e9", color: "#2e7d32" },
  unmatched: { bg: "#fce4ec", color: "#c62828" },
  ignored: { bg: "#eceff1", color: "#455a64" },
  expired: { bg: "#efebe9", color: "#4e342e" },
  closed: { bg: "#eceff1", color: "#455a64" },
  pending: { bg: "#fff3e0", color: "#e65100" },
  rejected: { bg: "#eceff1", color: "#455a64" },
  no_device: { bg: "#fffde7", color: "#8d6e00" },
  failed: { bg: "#fce4ec", color: "#c62828" },
};

export const walletTotal = (w: any): number => Number(w.available_minor ?? 0) + Number(w.swept_unspent_minor ?? 0);

export const gbp = (minor: number | string | null | undefined): string => {
  if (minor == null || minor === "" || isNaN(Number(minor))) return "—";
  return `£${(Number(minor) / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const bpsToPct = (bps: number | null | undefined): string =>
  bps == null ? "—" : `${Number(bps) / 100}%`;

export const fmtDate = (v: string | null | undefined): string =>
  v ? moment(v).format("D MMM YYYY") : "—";

export const fmtDateTime = (v: string | null | undefined): string =>
  v ? moment(v).format("D MMM YYYY, HH:mm") : "—";

export const fmtPeriod = (p: string | null | undefined): string =>
  p ? moment(p, "YYYY-MM").format("MMM YYYY") : "—";

export const errorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const fields = data?.errors && typeof data.errors === "object" ? Object.values(data.errors) : [];
  const base = data?.message || fallback;
  return fields.length ? `${base}: ${fields.join(", ")}` : base;
};

export const humanize = (v: string | null | undefined): string => (v ? v.replace(/_/g, " ") : "—");

export const listOf = (body: any): any[] => (Array.isArray(body) ? body : body?.data || []);

export const monthOptions = (count = 24): { value: string; label: string }[] =>
  Array.from({ length: count }, (_v, i) => {
    const m = moment().startOf("month").subtract(i, "months");
    return { value: m.format("YYYY-MM"), label: m.format("MMM YYYY") };
  });

export const previousMonth = (): string => moment().subtract(1, "month").format("YYYY-MM");

export const useCloStyles = makeStyles((theme: Theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#f5f5f5",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  headerActions: { display: "flex", gap: theme.spacing(1.5), alignItems: "center" },
  tabActions: { display: "flex", justifyContent: "flex-end", gap: theme.spacing(1.5), marginBottom: theme.spacing(2) },
  title: { fontWeight: 600 },
  subtitle: { color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  filterField: { minWidth: 180 },
  filterButton: { height: 40, minWidth: 96 },
  tableContainer: { borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  tableHead: { backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : "#fafafa" },
  tableHeadCell: { fontWeight: 600, color: theme.palette.text.primary, whiteSpace: "nowrap" },
  emptyCell: { padding: theme.spacing(4), color: theme.palette.text.secondary },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    margin: theme.spacing(3, 0, 1.5),
  },
  sectionTitle: { fontWeight: 600 },
  muted: { color: theme.palette.text.secondary },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" },
  dialogContent: { display: "grid", gap: theme.spacing(2), minWidth: 400, paddingTop: theme.spacing(1) },
  statusChip: { fontWeight: 600, fontSize: "0.75rem" },
  statRow: { display: "flex", gap: theme.spacing(2), marginBottom: theme.spacing(2), flexWrap: "wrap" },
  statCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    padding: theme.spacing(2, 3),
    minWidth: 150,
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  statValue: { fontSize: 24, fontWeight: 600, color: "#e91e8c" },
  statLabel: { fontSize: 12, color: theme.palette.text.secondary, textTransform: "uppercase" },
  warnRow: { backgroundColor: theme.palette.type === "dark" ? "rgba(198, 40, 40, 0.18)" : "#fff5f5" },
  totalRow: { "& td": { fontWeight: 600, borderTop: `2px solid ${theme.palette.divider}` } },
  clickableRow: { cursor: "pointer" },
}));

export const StatusChip = ({ status }: { status: string }) => {
  const classes = useCloStyles();
  const colors = STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#333" };
  return (
    <Chip
      label={humanize(status)}
      size="small"
      className={classes.statusChip}
      style={{ backgroundColor: colors.bg, color: colors.color }}
    />
  );
};

export const StatCard = ({ label, value }: { label: string; value: React.ReactNode }) => {
  const classes = useCloStyles();
  return (
    <div className={classes.statCard}>
      <div className={classes.statValue}>{value}</div>
      <div className={classes.statLabel}>{label}</div>
    </div>
  );
};

export const InTabsContext = React.createContext(false);

export const PageHeader = ({
  title,
  subtitle,
  actions,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}) => {
  const classes = useCloStyles();
  const inTabs = React.useContext(InTabsContext);
  if (inTabs) return actions ? <div className={classes.tabActions}>{actions}</div> : null;
  return (
    <div className={classes.header}>
      <div>
        <Typography variant="h5" className={classes.title}>{title}</Typography>
        {subtitle && <Typography variant="body2" className={classes.subtitle}>{subtitle}</Typography>}
      </div>
      {actions && <div className={classes.headerActions}>{actions}</div>}
    </div>
  );
};

export const SectionHeader = ({ title, meta }: { title: string; meta?: React.ReactNode }) => {
  const classes = useCloStyles();
  return (
    <div className={classes.sectionHeader}>
      <Typography variant="h6" className={classes.sectionTitle}>{title}</Typography>
      {meta && <Typography variant="body2" className={classes.muted}>{meta}</Typography>}
    </div>
  );
};

export const FilterSelect = ({
  label,
  value,
  onChange,
  options,
  allLabel = "All",
  fullWidth,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  allLabel?: string | null;
  fullWidth?: boolean;
}) => {
  const classes = useCloStyles();
  return (
    <FormControl variant="outlined" size="small" fullWidth={fullWidth} className={fullWidth ? undefined : classes.filterField}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={(e) => onChange(e.target.value as string)} label={label}>
        {allLabel !== null && <MenuItem value="">{allLabel}</MenuItem>}
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const plainOptions = (values: string[]) => values.map((v) => ({ value: v, label: v }));

export const DateField = ({
  label,
  value,
  onChange,
  className,
  required,
  fullWidth,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  required?: boolean;
  fullWidth?: boolean;
}) => (
  <MuiPickersUtilsProvider utils={MomentUtils}>
    <DatePicker
      label={label}
      format="D MMM YYYY"
      inputVariant="outlined"
      size="small"
      autoOk
      clearable={!required}
      required={required}
      fullWidth={fullWidth}
      className={className}
      value={value ? moment(value, "YYYY-MM-DD") : null}
      onChange={(date: any) => onChange(date ? date.format("YYYY-MM-DD") : "")}
    />
  </MuiPickersUtilsProvider>
);

export type Column = { label: string; numeric?: boolean };

export const DataTable = ({
  columns,
  empty,
  isEmpty,
  children,
  footer,
}: {
  columns: Column[];
  empty: string;
  isEmpty: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) => {
  const classes = useCloStyles();
  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.label} align={c.numeric ? "right" : "left"} className={classes.tableHeadCell}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isEmpty ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" className={classes.emptyCell}>{empty}</TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
      </Table>
      {footer}
    </TableContainer>
  );
};

export const ServerPagination = ({
  page,
  total,
  onChange,
}: {
  page: number;
  total: number;
  onChange: (page: number) => void;
}) => (
  <TablePagination
    component="div"
    count={total}
    page={Math.max(0, page - 1)}
    rowsPerPage={PAGE_SIZE}
    rowsPerPageOptions={[PAGE_SIZE]}
    onChangePage={(_e, p) => onChange(p + 1)}
  />
);

export const LocalPagination = ({
  count,
  page,
  pageSize,
  sizes,
  onPage,
  onPageSize,
}: {
  count: number;
  page: number;
  pageSize: number;
  sizes: number[];
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) => (
  <TablePagination
    component="div"
    count={count}
    page={page}
    rowsPerPage={pageSize}
    rowsPerPageOptions={sizes}
    onChangePage={(_e, p) => onPage(p)}
    onChangeRowsPerPage={(e) => onPageSize(Number(e.target.value))}
  />
);
